import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";

interface KeywordInfo {
  word: string;
  idInternal: number;
  word_equivalent: {
    word1: string;
    word2: string;
    word3: string;
    word4: string;
  };
}

interface SpeechToCheckContentData {
  keywords_info: KeywordInfo[];
  intro_text?: string;
}

interface SpeechToCheckDetailProps {
  data: {
    data_speech_to_check?: {
      response: string;
      keywords_detected: string[];
    };
  };
  contentId?: number;
}

const SpeechToCheckDetail = ({ data, contentId }: SpeechToCheckDetailProps) => {
  const { t } = useTranslation();
  const [contentData, setContentData] = useState<SpeechToCheckContentData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  const speechData = data?.data_speech_to_check;
  const transcript = speechData?.response || "";
  const detectedKeywords = speechData?.keywords_detected || [];

  // Fetch content to get all expected keywords
  useEffect(() => {
    const fetchContent = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${apiUrls.base}/api/content-speech-to-checks?filters[content][id][$eq]=${contentId}`,
        );

        const results = response.data?.data || response.data;
        const content = Array.isArray(results) ? results[0] : results;

        if (content) {
          const attrs = content.attributes || content;
          setContentData({
            keywords_info: attrs.keywords_info || [],
            intro_text: attrs.intro_text,
          });
        }
      } catch (err) {
        console.error("Error fetching speech to check content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchContent();
  }, [contentId]);

  // Build keywords list with found status - includes duplicates for score visibility
  const allKeywords = (() => {
    if (!contentData?.keywords_info) return [];

    // Track which words have been seen for deduplication display
    const seenWords = new Set<string>();
    const result: {
      word: string;
      equivalents: string[];
      found: boolean;
      matchedWord: string | null;
      allVariants: string[];
      isDuplicate: boolean;
    }[] = [];

    for (const kw of contentData.keywords_info) {
      const wordLower = kw.word.toLowerCase();
      const isDuplicate = seenWords.has(wordLower);

      // Build all variants (main word + equivalents) with original casing preserved
      const allVariantsWithCase = [
        kw.word,
        kw.word_equivalent?.word1,
        kw.word_equivalent?.word2,
        kw.word_equivalent?.word3,
        kw.word_equivalent?.word4,
      ].filter(Boolean) as string[];

      const allVariants = allVariantsWithCase.map((v) => v.toLowerCase());

      // Check if any variant was detected by the backend
      const matchedDetected = detectedKeywords.find((detected) =>
        allVariants.includes(detected.toLowerCase()),
      );
      const found = !!matchedDetected;

      // Find which word actually appears in the transcript
      let actualMatchedWord: string | null = null;
      if (found) {
        for (const variant of allVariantsWithCase) {
          const escapedVariant = variant.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
          const regex = new RegExp(`\\b${escapedVariant}\\b`, "i");
          if (regex.test(transcript)) {
            actualMatchedWord = variant;
            break;
          }
        }
        if (!actualMatchedWord) {
          actualMatchedWord = matchedDetected || null;
        }
      }

      // Get all non-empty equivalents for display (excluding the main word itself)
      const equivalents = isDuplicate
        ? [] // Don't show equivalents for duplicates
        : [
            kw.word_equivalent?.word1,
            kw.word_equivalent?.word2,
            kw.word_equivalent?.word3,
            kw.word_equivalent?.word4,
          ].filter((eq): eq is string => Boolean(eq) && eq.toLowerCase() !== wordLower);

      // Mark this word and all its equivalents as seen (only for first occurrence)
      if (!isDuplicate) {
        seenWords.add(wordLower);
        equivalents.forEach((eq) => seenWords.add(eq.toLowerCase()));
      }

      result.push({
        word: kw.word,
        equivalents,
        found,
        matchedWord: actualMatchedWord,
        allVariants: [kw.word, ...equivalents],
        isDuplicate,
      });
    }

    return result;
  })();

  // For highlighting, only use unique keywords
  const uniqueKeywords = allKeywords.filter((k) => !k.isDuplicate);

  // Highlight keywords in transcript
  const highlightTranscript = () => {
    if (!transcript || uniqueKeywords.length === 0) {
      return <span>{transcript}</span>;
    }

    const foundKeywords = uniqueKeywords.filter((k) => k.found);
    if (foundKeywords.length === 0) {
      return <span>{transcript}</span>;
    }

    // Build list of all words to highlight (main word + equivalents)
    const allWords: string[] = [];
    foundKeywords.forEach((kw) => {
      allWords.push(...kw.allVariants);
    });

    // Sort by length (longest first) to avoid partial replacements
    allWords.sort((a, b) => b.length - a.length);

    // Create a map of keyword positions
    const highlights: { start: number; end: number }[] = [];

    allWords.forEach((word) => {
      // Escape special regex characters
      const escapedWord = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`\\b${escapedWord}\\b`, "gi");
      let match;
      while ((match = regex.exec(transcript)) !== null) {
        // Store match values before callback to avoid non-null assertions
        const matchIndex = match.index;
        const matchLength = match[0].length;
        // Check if this position already has a highlight
        const overlaps = highlights.some(
          (h) =>
            (matchIndex >= h.start && matchIndex < h.end) ||
            (matchIndex + matchLength > h.start && matchIndex + matchLength <= h.end),
        );
        if (!overlaps) {
          highlights.push({
            start: matchIndex,
            end: matchIndex + matchLength,
          });
        }
      }
    });

    // Sort highlights by position
    highlights.sort((a, b) => a.start - b.start);

    // Build the highlighted text
    const parts: JSX.Element[] = [];
    let lastEnd = 0;

    highlights.forEach((highlight, index) => {
      // Add text before this highlight
      if (highlight.start > lastEnd) {
        parts.push(<span key={`text-${index}`}>{transcript.slice(lastEnd, highlight.start)}</span>);
      }

      // Add highlighted keyword (green underline style)
      parts.push(
        <span
          key={`highlight-${index}`}
          className="text-green-600 font-medium underline decoration-green-500 decoration-2 underline-offset-2"
        >
          {transcript.slice(highlight.start, highlight.end)}
        </span>,
      );

      lastEnd = highlight.end;
    });

    // Add remaining text
    if (lastEnd < transcript.length) {
      parts.push(<span key="text-end">{transcript.slice(lastEnd)}</span>);
    }

    return parts;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!transcript) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  return (
    <div className="border-2 border-teal-200 rounded-xl p-5">
      {/* Intro text (like a question) */}
      {contentData?.intro_text && (
        <div className="mb-4 pb-4 border-b border-gray-200">
          <p className="text-sm font-medium text-gray-900">{contentData.intro_text}</p>
        </div>
      )}

      {/* Transcript header */}
      <h4 className="text-base font-semibold text-gray-900 mb-1">
        {t("statsNew.interaction.speechToCheck.transcript")}
      </h4>

      {/* Transcript text with highlights */}
      <div className="mb-6">
        <p className="text-sm text-gray-700 leading-relaxed">{highlightTranscript()}</p>
      </div>

      {/* Keywords line */}
      {allKeywords.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-1 gap-y-1 text-sm">
          <span className="text-gray-600 font-medium">
            {t("statsNew.interaction.speechToCheck.keywords")} :
          </span>
          {allKeywords.map((keyword, index) => {
            // Check if the matched word is the main keyword or a synonym
            const matchedIsMainWord =
              keyword.matchedWord?.toLowerCase() === keyword.word.toLowerCase();

            // Duplicate keywords are shown with reduced opacity and italic
            if (keyword.isDuplicate) {
              return (
                <span key={index} className="inline-flex items-center opacity-60 italic">
                  {keyword.found ? (
                    <span className="text-green-600">{keyword.word}</span>
                  ) : (
                    <span className="text-red-500 line-through">{keyword.word}</span>
                  )}
                  <span className="text-gray-400 text-xs ml-0.5">
                    ({t("statsNew.interaction.speechToCheck.duplicate")})
                  </span>
                  {index < allKeywords.length - 1 && <span className="text-gray-400">,&nbsp;</span>}
                </span>
              );
            }

            return (
              <span key={index} className="inline-flex items-center">
                {keyword.found ? (
                  <>
                    {keyword.equivalents.length > 0 ? (
                      matchedIsMainWord ? (
                        // Main keyword was matched directly
                        <span className="text-gray-500">
                          [<span className="text-green-600 font-medium">{keyword.word}</span>
                          <span className="text-gray-400"> : {keyword.equivalents.join(", ")}</span>
                          ]
                        </span>
                      ) : (
                        // A synonym was matched - show which one
                        <span className="text-gray-500">
                          [<span className="text-gray-500">{keyword.word}</span>
                          <span className="text-gray-400"> : </span>
                          {keyword.equivalents.map((eq, eqIndex) => {
                            const isMatched =
                              eq.toLowerCase() === keyword.matchedWord?.toLowerCase();
                            return (
                              <span key={eqIndex}>
                                {isMatched ? (
                                  <span className="text-green-600 font-medium">{eq}</span>
                                ) : (
                                  <span className="text-gray-400">{eq}</span>
                                )}
                                {eqIndex < keyword.equivalents.length - 1 && (
                                  <span className="text-gray-400">, </span>
                                )}
                              </span>
                            );
                          })}
                          ]
                        </span>
                      )
                    ) : (
                      <span className="text-green-600 font-medium">{keyword.word}</span>
                    )}
                  </>
                ) : (
                  <span className="text-red-500 line-through">{keyword.word}</span>
                )}
                {index < allKeywords.length - 1 && <span className="text-gray-400">,&nbsp;</span>}
              </span>
            );
          })}
        </div>
      )}

      {/* Legend for keyword format */}
      {allKeywords.length > 0 && (
        <div className="mt-3 text-xs text-gray-400">
          <span className="text-green-600">●</span>{" "}
          {t("statsNew.interaction.speechToCheck.legendFound")}
          {" · "}
          <span className="text-red-500">●</span>{" "}
          {t("statsNew.interaction.speechToCheck.legendMissing")}
          {allKeywords.some((k) => k.equivalents.length > 0) && (
            <>
              {" · "}
              {t("statsNew.interaction.speechToCheck.legendSynonyms")}
            </>
          )}
          {allKeywords.some((k) => k.isDuplicate) && (
            <>
              {" · "}
              <span className="italic">
                {t("statsNew.interaction.speechToCheck.legendDuplicate")}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SpeechToCheckDetail;
